import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { RelocationProcessApiAdapter, type CreateLinePayload } from '../adapters/RelocationProcessApiAdapter';
import { mapRelocationErrorMessage } from '../adapters/errorMapper';
import {
  resolveBatchIdForProduct,
  resolvedLocationHasInventory,
  resolveProductScanFromLocation,
  type RelocationDetail,
  type RelocationLine,
  type RelocationStep,
  type ResolvedCart,
  type ResolvedLocation,
  type ResolvedProduct,
} from '../models';

export interface ScanResult {
  success: boolean;
  message: string;
}

export interface ConfirmQuantityResult extends ScanResult {
  line?: RelocationLine;
}

interface UseRelocationFlowParams {
  relocation: RelocationDetail | null;
  /** Lanzado cuando la reubicación es recién creada; útil para navegar a /relocations/:id */
  onRelocationCreated?: (id: number) => void;
  /** Notifica al hook padre para refrescar/actualizar el detalle */
  onLineAdded?: (line: RelocationLine) => void;
  /** Tras despachar correctamente */
  onDispatched?: () => void;
}

/**
 * Máquina de estados del flujo Reubicar.
 *
 * Pasos: cart → location → product → quantity → (repetir location)
 * Finaliza con POST /relocations/:id/dispatch.
 */
export const useRelocationFlow = ({
  relocation,
  onRelocationCreated,
  onLineAdded,
  onDispatched,
}: UseRelocationFlowParams) => {
  const { t } = useTranslation('relocationProcessModule');
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new RelocationProcessApiAdapter(httpClient), [httpClient]);

  const [cart, setCart] = useState<ResolvedCart | undefined>(undefined);
  const [selectedLocation, setSelectedLocation] = useState<ResolvedLocation | undefined>(undefined);
  const [selectedProduct, setSelectedProduct] = useState<ResolvedProduct | undefined>(undefined);
  const [isDispatching, setIsDispatching] = useState(false);

  const relocationId = relocation?.id;

  // Al pasar a "/relocations/new" (sin relocationId) limpiamos todo el estado del flujo.
  // Esto evita arrastrar carrito/ubicación/producto desde una reubicación anterior.
  useEffect(() => {
    if (relocationId) return;
    setCart(undefined);
    setSelectedLocation(undefined);
    setSelectedProduct(undefined);
  }, [relocationId]);

  // Si ya hay relocation cargada y todavía no tenemos cart, resolvemos el carrito.
  useEffect(() => {
    const loadCart = async () => {
      if (!relocation) return;
      if (cart) return;
      // Backend puede devolver carrito embebido
      if (relocation.cart && relocation.cart.code) {
        setCart({ id: relocation.cart.id, code: relocation.cart.code });
        return;
      }
      if (relocation.cart_id) {
        const resolved = await adapter.getCartById(relocation.cart_id);
        if (resolved) setCart(resolved);
      }
    };
    loadCart();
  }, [relocation, cart, adapter]);

  const currentStep: RelocationStep = useMemo(() => {
    if (!relocationId || !cart) return 'cart';
    if (!selectedLocation) return 'location';
    if (!selectedProduct) return 'product';
    return 'quantity';
  }, [relocationId, cart, selectedLocation, selectedProduct]);

  const processScannedCode = useCallback(
    async (rawCode: string): Promise<ScanResult> => {
      const code = rawCode.trim();
      if (!code) return { success: false, message: 'Código vacío' };

      // Paso 1: Escanear carrito → POST /relocations
      if (!relocationId || !cart) {
        const foundCart = await adapter.findCartByCode(code);
        if (!foundCart) {
          return { success: false, message: `No se encontró ningún carrito con el código "${code}"` };
        }
        try {
          const created = await adapter.createRelocation({ cart_id: foundCart.id });
          setCart(foundCart);
          onRelocationCreated?.(created.id);
          return {
            success: true,
            message: `Reubicación iniciada con carrito ${foundCart.code}`,
          };
        } catch (error: any) {
          return {
            success: false,
            message: mapRelocationErrorMessage(error, { t }),
          };
        }
      }

      // Paso 2: Escanear ubicación
      if (!selectedLocation) {
        const foundLocation = await adapter.findLocationByCode(code);
        if (!foundLocation) {
          return { success: false, message: `Ubicación "${code}" no encontrada` };
        }
        if (!resolvedLocationHasInventory(foundLocation)) {
          return { success: false, message: t('errors.location_no_inventory') };
        }
        setSelectedLocation(foundLocation);
        return { success: true, message: `Ubicación ${foundLocation.code} seleccionada` };
      }

      // Paso 3: Escanear producto → solo `locationProducts` de la ubicación (sin GET /products)
      if (!selectedProduct) {
        const foundProduct = resolveProductScanFromLocation(selectedLocation, code);
        if (!foundProduct) {
          return { success: false, message: t('errors.product_not_in_location_scan', { code }) };
        }
        setSelectedProduct(foundProduct);
        const pickedLabel = foundProduct.name?.trim() || foundProduct.code?.trim();
        return {
          success: true,
          message: pickedLabel
            ? t('toasts.product_picked', { label: pickedLabel })
            : t('toasts.product_picked_fallback'),
        };
      }

      return { success: false, message: 'Confirma la cantidad para continuar' };
    },
    [relocationId, cart, selectedLocation, selectedProduct, adapter, onRelocationCreated, t],
  );

  const confirmQuantity = useCallback(
    async (qty: number): Promise<ConfirmQuantityResult> => {
      if (!relocationId || !selectedLocation || !selectedProduct) {
        return { success: false, message: 'No hay producto listo para confirmar' };
      }
      if (!Number.isFinite(qty) || qty <= 0) {
        return { success: false, message: 'Cantidad inválida' };
      }
      try {
        const batchId = resolveBatchIdForProduct(selectedLocation.stock_batches, selectedProduct.id);
        const payload: CreateLinePayload = {
          product_id: selectedProduct.id,
          from_location_id: selectedLocation.id,
          qty,
        };
        if (batchId != null && Number.isFinite(batchId)) {
          payload.batch_id = batchId;
        }

        const responseLine = await adapter.addLine(relocationId, payload);

        const sbEntry =
          batchId != null ? selectedLocation.stock_batches?.find((s) => s.batch_id === batchId) : undefined;

        // El backend puede responder 204 (sin body). En ese caso construimos una línea optimista
        // para evitar renderizar "Producto ID: undefined" hasta el refetch.
        const line: RelocationLine =
          responseLine ??
          ({
            id: -Date.now(),
            relocation_id: relocationId,
            product_id: selectedProduct.id,
            location_id: selectedLocation.id,
            from_location_id: selectedLocation.id,
            qty,
            ...(batchId != null ? { batch_id: batchId } : { batch_id: null }),
            ...(batchId != null && sbEntry?.batch
              ? {
                  batch: {
                    id: batchId,
                    batch_number: sbEntry.batch.batch_number ?? null,
                    expiry_date: sbEntry.batch.expiry_date ?? null,
                  },
                }
              : {}),
            product: {
              id: selectedProduct.id,
              code: selectedProduct.code,
              name: selectedProduct.name,
              image: selectedProduct.image,
            },
            location: {
              id: selectedLocation.id,
              code: selectedLocation.code,
            },
          } as RelocationLine);

        onLineAdded?.(line);
        // Volver al paso de escanear ubicación
        setSelectedLocation(undefined);
        setSelectedProduct(undefined);
        return { success: true, message: `Se registraron ${qty} unidad(es)`, line };
      } catch (error: any) {
        return {
          success: false,
          message: mapRelocationErrorMessage(error, {
            t,
            locationCode: selectedLocation.code,
            productCode: selectedProduct.code || String(selectedProduct.id),
            qty,
          }),
        };
      }
    },
    [relocationId, selectedLocation, selectedProduct, adapter, onLineAdded, t],
  );

  const goBackToLocationStep = useCallback(() => {
    setSelectedLocation(undefined);
    setSelectedProduct(undefined);
  }, []);

  const goBackToProductStep = useCallback(() => {
    setSelectedProduct(undefined);
  }, []);

  const dispatchRelocation = useCallback(async (): Promise<ScanResult> => {
    if (!relocationId) return { success: false, message: 'Sin reubicación activa' };
    setIsDispatching(true);
    try {
      await adapter.dispatchRelocation(relocationId);
      onDispatched?.();
      return { success: true, message: 'Reubicación despachada correctamente' };
    } catch (error: any) {
      return {
        success: false,
        message: mapRelocationErrorMessage(error, { t }),
      };
    } finally {
      setIsDispatching(false);
    }
  }, [relocationId, adapter, onDispatched, t]);

  return {
    cart,
    selectedLocation,
    selectedProduct,
    currentStep,
    isDispatching,
    processScannedCode,
    confirmQuantity,
    goBackToLocationStep,
    goBackToProductStep,
    dispatchRelocation,
  };
};
